import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useIntl } from 'react-intl';
import VariantSelection from '../VariantSelection';
import messages from './messages';
import styles from './ProductVariantSelection.module.scss';
import QuantityCounter from '../../../components/common/QuantityCounter';
import { VARIANT_ATTRIBUTE_NAME_PILLOW_SIZE } from '../VariantSelection/constants';
import AddASecondFor from '../../../components/common/AddASecondFor';
import { findVariantAttributeByName } from '../../../utils/variants';

const PRODUCT_TYPES_SINGULAR = {
  pillow: 'pillow',
  mattress: 'mattress',
  bedFrames: 'bed_frames',
  sheets: 'sheet',
  defaultProductName: 'product',
};

const ProductVariantSelection = ({
  onFirstProductQuantityIncrement,
  onFirstProductQuantityDecrement,
  onSelectFirstSKU,
  onSelectSecondSKU,
  product,
  selectedFirstSKU = undefined,
  selectedFirstSKUQuantity,
  selectedSecondSKU = undefined,
  selectedFirstSKUDiscount = undefined,
}) => {
  const { formatMessage } = useIntl();

  const showQuantityCheckbox = product?.secondItemDiscount?.percentage;

  const productTypesWithQuantityCounter = [
    PRODUCT_TYPES_SINGULAR.mattress,
    PRODUCT_TYPES_SINGULAR.bedFrames,
  ];
  const showQuantityCounter = !productTypesWithQuantityCounter.includes(
    product?.productTypeSingular,
  );

  const isPillow = !!findVariantAttributeByName(
    product.variants[0],
    VARIANT_ATTRIBUTE_NAME_PILLOW_SIZE,
  );

  const isMattress = product.productTypeSingular === 'mattress';

  return (
    <>
      <div className={styles['variant-selection-container']}>
        <VariantSelection
          variants={product.variants}
          productKey={product.key}
          productName={product.name}
          attributesExtraInfo={product.attributesExtraInfo}
          attributesUpperTitle={
            selectedSecondSKU &&
            formatMessage(messages.firstProductAttributesUpperTitle, {
              productName: product.productTypeSingular,
            })
          }
          isShowingDiscountedPrices
          defaultSelectedSKU={selectedFirstSKU}
          onSelectVariant={({ sku }) => {
            onSelectFirstSKU(sku);
          }}
          discountSavings={isMattress ? selectedFirstSKUDiscount : undefined}
        />
      </div>

      {showQuantityCheckbox ? (
        <>
          <AddASecondFor
            id={`variant-selector-second-product-checkbox-${product.key}`}
            isChecked={!!selectedSecondSKU}
            preAddedLabel={product.secondItemDiscount.preAddedMessage}
            postAddedLabel={product.secondItemDiscount.postAddedMessage}
            onChange={() => {
              onSelectSecondSKU(
                selectedSecondSKU ? undefined : selectedFirstSKU,
              );
            }}
            variants={product.variants}
            attributeNameToFilter={
              isPillow ? VARIANT_ATTRIBUTE_NAME_PILLOW_SIZE : undefined
            }
            primarySelectedSKU={selectedFirstSKU}
            percentDiscount={product.secondItemDiscount.percentage}
            onSelectVariant={({ sku }) => {
              onSelectSecondSKU(sku);
            }}
            isShowingDiscountedPrices={!!selectedSecondSKU}
            attributesExtraInfo={product.attributesExtraInfo}
          />
        </>
      ) : (
        showQuantityCounter && (
          <div
            className={classnames(styles['quantity-container'], [
              styles['quantitiy-container-margin'],
            ])}
          >
            <div className={styles['quantity-text']}>
              {formatMessage(messages.selectQuantity)}
            </div>
            <QuantityCounter
              theme="pdp"
              quantity={selectedFirstSKUQuantity}
              isDecrementButtonDisabled={selectedFirstSKUQuantity === 1}
              onQuantityIncrement={onFirstProductQuantityIncrement}
              onQuantityDecrement={onFirstProductQuantityDecrement}
            />
          </div>
        )
      )}
    </>
  );
};

ProductVariantSelection.propTypes = {
  product: PropTypes.shape({
    name: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    secondItemDiscount: PropTypes.shape({
      percentage: PropTypes.number,
      preAddedMessage: PropTypes.string,
      postAddedMessage: PropTypes.string,
    }),
    variants: PropTypes.arrayOf(
      PropTypes.shape({
        sku: PropTypes.string.isRequired,
        masterVariant: PropTypes.bool,
        attributes: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string.isRequired,
            key: PropTypes.string,
            label: PropTypes.string,
          }),
        ),
      }),
    ).isRequired,
    productTypeSingular: PropTypes.string.isRequired,
    attributesExtraInfo: PropTypes.arrayOf(
      PropTypes.shape({
        attributeName: PropTypes.string,
        isToolTip: PropTypes.bool,
        text: PropTypes.string,
        content: PropTypes.string,
      }),
    ),
  }).isRequired,
  selectedFirstSKU: PropTypes.string,
  onSelectFirstSKU: PropTypes.func.isRequired,
  onFirstProductQuantityIncrement: PropTypes.func.isRequired,
  onFirstProductQuantityDecrement: PropTypes.func.isRequired,
  selectedFirstSKUQuantity: PropTypes.number.isRequired,
  selectedSecondSKU: PropTypes.string,
  onSelectSecondSKU: PropTypes.func.isRequired,
  selectedFirstSKUDiscount: PropTypes.string,
};

export default ProductVariantSelection;
